import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FileText, Sparkles, Save, RefreshCw } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { toast } from 'sonner';
import { infantilAPI } from '../services/api';

const API_URL = window.location.origin || '';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' });
const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.35 } } };

export default function ReportEditorPage() {
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('');
  const [tone, setTone] = useState('acolhedor');
  const [content, setContent] = useState('');
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [obsCount, setObsCount] = useState(0);

  useEffect(() => { infantilAPI.classes().then(setClasses); }, []);
  useEffect(() => { if (selectedClass) infantilAPI.classStudents(selectedClass).then(setStudents); }, [selectedClass]);
  useEffect(() => {
    if (selectedStudent) {
      infantilAPI.observations.list({ student_id: selectedStudent, limit: 200 }).then(d => setObsCount(d.total || 0));
    }
  }, [selectedStudent]);

  const studentName = students.find(s => s.id === selectedStudent)?.name || '';

  const handleGenerate = async () => {
    if (!selectedStudent) { toast.error('Selecione uma criança'); return; }
    setGenerating(true);
    try {
      const obs = await infantilAPI.observations.list({ student_id: selectedStudent, limit: 50 });
      const summary = (obs.items || []).map(o => `[${o.context}] ${o.title}: ${o.observation_text?.substring(0, 150)}${o.child_speech ? ` Fala: "${o.child_speech}"` : ''}`).join('\n');
      const res = await fetch(`${API_URL}/api/infantil360/ai/report`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ student_name: studentName, observations_summary: summary || 'Sem observações registradas.', tone, trimester: '1º Trimestre' })
      }).then(r => r.json());
      if (res.ok) { setContent(res.report); toast.success('Relatório gerado pela IA!'); }
      else toast.error('Erro ao gerar');
    } catch { toast.error('Erro de conexão'); }
    finally { setGenerating(false); }
  };

  const handleSave = async () => {
    if (!content || !selectedStudent) return;
    setSaving(true);
    try {
      await infantilAPI.reports.create({ student_id: selectedStudent, class_id: selectedClass, content, tone, status: 'draft' });
      toast.success('Relatório salvo como rascunho!');
    } catch { toast.error('Erro ao salvar'); }
    finally { setSaving(false); }
  };

  return (
    <motion.div className="space-y-6" initial="hidden" animate="visible" variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.05 } } }}
      data-testid="report-editor-page">
      <motion.div variants={fadeUp}>
        <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2"><FileText className="h-5 w-5 text-[#EC4899]" /> Relatórios Narrativos</h1>
        <p className="text-sm text-slate-500 mt-1">Gere relatórios pedagógicos com assistência de IA</p>
      </motion.div>

      <motion.div variants={fadeUp} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label className="text-xs">Turma</Label>
            <select value={selectedClass} onChange={e => { setSelectedClass(e.target.value); setSelectedStudent(''); setContent(''); }}
              className="w-full h-10 rounded-xl border border-slate-200 px-3 text-sm">
              <option value="">Selecione...</option>
              {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Criança</Label>
            <select value={selectedStudent} onChange={e => { setSelectedStudent(e.target.value); setContent(''); }}
              className="w-full h-10 rounded-xl border border-slate-200 px-3 text-sm">
              <option value="">Selecione...</option>
              {students.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Tom do relatório</Label>
            <select value={tone} onChange={e => setTone(e.target.value)}
              className="w-full h-10 rounded-xl border border-slate-200 px-3 text-sm">
              <option value="acolhedor">Acolhedor</option>
              <option value="técnico">Técnico (BNCC)</option>
              <option value="família">Para Família</option>
            </select>
          </div>
          <div className="flex items-end">
            <Button onClick={handleGenerate} disabled={generating || !selectedStudent} className="w-full rounded-xl"
              style={{ background: 'linear-gradient(135deg, #64327D, #8E52AB)' }}>
              <Sparkles className="h-4 w-4 mr-2" /> {generating ? 'Gerando...' : 'Gerar com IA'}
            </Button>
          </div>
        </div>
        {selectedStudent && <p className="text-xs text-slate-400 mt-2">{obsCount} observações disponíveis para {studentName}</p>}
      </motion.div>

      {/* Editor */}
      <motion.div variants={fadeUp} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-slate-800">Editor do Relatório</h3>
          <div className="flex gap-2">
            {content && <Button variant="outline" size="sm" onClick={handleGenerate} disabled={generating} className="rounded-xl text-xs">
              <RefreshCw className="h-3 w-3 mr-1" /> Regenerar
            </Button>}
            <Button size="sm" onClick={handleSave} disabled={saving || !content} className="rounded-xl text-xs"
              style={{ background: 'linear-gradient(135deg, #2BB673, #1a8f5a)' }}>
              <Save className="h-3 w-3 mr-1" /> {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </div>
        <textarea value={content} onChange={e => setContent(e.target.value)}
          placeholder={selectedStudent ? 'Clique em "Gerar com IA" ou escreva o relatório aqui...' : 'Selecione uma criança para começar...'}
          className="w-full h-64 rounded-xl border border-slate-200 px-4 py-3 text-sm resize-none focus:ring-2 focus:ring-[#64327D]/20 leading-relaxed" />
      </motion.div>
    </motion.div>
  );
}
